import React from 'react';
import { FaUserFriends, FaCalendarAlt, FaClock, FaPlus, FaMinus, FaChevronDown } from 'react-icons/fa';

const DEFAULT_SLOTS = ["09:00", "10:00", "11:00", "14:00", "15:00", "16:00"];

const VisitorSelection = ({
  visitors,
  incrementVisitor,
  decrementVisitor,
  selectedDate,
  setSelectedDate,
  selectedTime,
  setSelectedTime,
  selectedOption,
  calculateTotalPrice,
  loading,
  timeSlots = DEFAULT_SLOTS,
  slotsFromApi = [],
  selectedSlot,
  setSelectedSlot,
}) => {
  const slots = (timeSlots && timeSlots.length > 0) ? timeSlots : DEFAULT_SLOTS;
  const useSlotBased = Array.isArray(slotsFromApi) && slotsFromApi.length > 0;
  const totalVisitors = visitors.reduce((sum, v) => sum + v.count, 0);
  const maxCapacity = selectedOption?.maxCapacity || 0;
  const toYMD = (d) => {
    if (!d) return undefined;
    const x = new Date(d);
    return isNaN(x.getTime()) ? undefined : x.toISOString().slice(0, 10);
  };
  const formatDisplayDate = (d) => {
    if (!d) return null;
    const x = new Date(d);
    return isNaN(x.getTime()) ? null : x.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
  };
  const minDate = toYMD(selectedOption?.bookingStartDate) || new Date().toISOString().split("T")[0];
  const maxDate = toYMD(selectedOption?.bookingEndDate) || undefined;
  const startDate = selectedOption?.bookingStartDate;
  const endDate = selectedOption?.bookingEndDate;
  const hasBookableRange = startDate || endDate;
  const bookableText = hasBookableRange
    ? startDate && endDate
      ? `${formatDisplayDate(startDate)} to ${formatDisplayDate(endDate)}`
      : startDate
      ? `From ${formatDisplayDate(startDate)}`
      : `Until ${formatDisplayDate(endDate)}`
    : null;

  return (
    <div className="space-y-8">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8">
        {/* Visitor Selection */}
        <div className="bg-white rounded-2xl border border-slate-200 p-5 sm:p-6 shadow-sm">
          <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
            <div className="flex items-center gap-2">
              <div className="p-2 bg-blue-100 rounded-lg">
                <FaUserFriends className="text-blue-600 text-lg" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-900">Select visitors</h3>
                <p className="text-slate-600 text-sm mt-0.5">Choose guests for each category</p>
              </div>
            </div>
            <div className="bg-slate-50 px-4 py-2.5 rounded-xl border border-slate-200">
              <span className="text-sm font-semibold text-slate-700">{totalVisitors} / {maxCapacity} guests</span>
            </div>
          </div>

          <div className="space-y-3">
            {visitors.map((visitor) => (
              <div
                key={visitor.typeId}
                className="flex items-center justify-between p-4 border border-slate-200 rounded-xl hover:border-blue-300 hover:bg-slate-50/50 transition-colors"
              >
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-semibold text-gray-900">
                      {visitor.type} ({visitor.minAge}-{visitor.maxAge})
                    </h4>
                  </div>
                  <div className="flex items-center text-sm text-gray-600">
                    <span className="mr-4">Price: {visitor.multiplier}x base rate</span>
                    {visitor.multiplier < 1 && (
                      <span className="px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs font-medium">
                        Discounted
                      </span>
                    )}
                  </div>
                </div>
                
                <div className="flex items-center gap-3 ml-4">
                  <button
                    type="button"
                    onClick={() => decrementVisitor(visitor.typeId)}
                    disabled={visitor.count === 0}
                    className="w-10 h-10 rounded-full border-2 border-slate-300 flex items-center justify-center hover:bg-slate-50 hover:border-slate-400 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    <FaMinus className="text-slate-600" />
                  </button>
                  <div className="w-12 text-center">
                    <div className="text-xl font-bold text-gray-900">{visitor.count}</div>
                    <div className="text-xs text-slate-500">guests</div>
                  </div>
                  <button
                    type="button"
                    onClick={() => incrementVisitor(visitor.typeId)}
                    disabled={totalVisitors >= maxCapacity}
                    className="w-10 h-10 rounded-full bg-blue-600 text-white flex items-center justify-center hover:bg-blue-700 shadow-md disabled:opacity-40 disabled:cursor-not-allowed transition-all"
                  >
                    <FaPlus />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Date & Time */}
        <div className="bg-white rounded-2xl border border-slate-200 p-5 sm:p-6 shadow-sm">
          <div className="flex items-center gap-2 mb-6">
            <div className="p-2 bg-indigo-100 rounded-lg">
              <FaCalendarAlt className="text-indigo-600 text-lg" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-gray-900">Date & time</h3>
              <p className="text-slate-600 text-sm mt-0.5">When do you want to go?</p>
            </div>
          </div>

          <div className="mb-4 p-3.5 bg-amber-50 border border-amber-200 rounded-xl">
            <p className="text-xs font-semibold text-amber-800 uppercase tracking-wide mb-1">Bookable dates</p>
            {bookableText ? (
              <>
                <p className="text-sm font-medium text-amber-900">{bookableText}</p>
                <p className="text-xs text-amber-700 mt-1">Pick a date within this range.</p>
              </>
            ) : (
              <p className="text-sm text-amber-800">No date restriction – you can book any future date.</p>
            )}
          </div>

          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Select Date
              </label>
              <div className="relative">
                <input
                  type="date"
                  value={selectedDate}
                  onChange={(e) => setSelectedDate(e.target.value)}
                  min={minDate}
                  max={maxDate}
                  className="w-full px-4 py-3.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none"
                />
                <FaChevronDown className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none" />
              </div>
              <p className="text-xs text-gray-500 mt-2">
                {selectedOption?.bookingStartDate || selectedOption?.bookingEndDate
                  ? "Bookable only within the date range set for this option"
                  : "Bookings available for the next 90 days"}
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                {useSlotBased ? "Select Time Slot" : "Select Time"}
              </label>
              {useSlotBased ? (
                <div className="space-y-2">
                  {slotsFromApi.map((slot) => {
                    const remaining = Number(slot.remaining) ?? Math.max(0, (slot.capacity || 0) - (slot.bookedCount || 0));
                    const isFull = remaining <= 0;
                    const isSelected = selectedSlot?._id === slot._id;
                    return (
                      <button
                        type="button"
                        key={slot._id}
                        onClick={() => !isFull && setSelectedSlot(slot)}
                        disabled={isFull}
                        className={`w-full py-3 px-4 rounded-xl border-2 text-left text-sm font-medium transition-all flex items-center justify-between ${
                          isFull
                            ? "border-slate-200 bg-slate-100 text-slate-400 cursor-not-allowed"
                            : isSelected
                            ? "border-blue-500 bg-blue-50 text-blue-800"
                            : "border-slate-200 hover:border-blue-300 hover:bg-slate-50 text-slate-700"
                        }`}
                      >
                        <span>
                          {slot.startTime} – {slot.endTime}
                          {slot.label ? ` · ${slot.label}` : ""}
                        </span>
                        <span className={isFull ? "text-slate-400" : "text-slate-600"}>
                          {isFull ? "Full" : `${remaining} left`}
                        </span>
                      </button>
                    );
                  })}
                </div>
              ) : (
                <div className="grid grid-cols-3 gap-3">
                  {slots.map((time) => (
                    <button
                      type="button"
                      key={time}
                      onClick={() => setSelectedTime(time)}
                      className={`py-2.5 rounded-xl border-2 text-sm font-medium transition-all ${
                        selectedTime === time ? "border-blue-500 bg-blue-50 text-blue-700" : "border-slate-200 hover:border-slate-300 hover:bg-slate-50 text-slate-700"
                      }`}
                    >
                      {time}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl p-4 border border-blue-100">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-white rounded-lg shadow-sm">
                  <FaClock className="text-blue-600" />
                </div>
                <div>
                  <h4 className="font-medium text-gray-900">Activity Duration</h4>
                  <p className="text-sm text-gray-600">
                    {selectedOption?.duration} minutes • Includes setup and safety briefing
                  </p>
                </div>
              </div>
            </div>

            <div className="pt-6 border-t border-gray-200">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-gray-600">Selected Time:</span>
                <span className="font-medium text-gray-900">
                  {useSlotBased && selectedSlot ? `${selectedSlot.startTime} – ${selectedSlot.endTime}` : selectedTime}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Total Guests:</span>
                <span className="font-bold text-lg text-gray-900">
                  {totalVisitors} {totalVisitors === 1 ? 'guest' : 'guests'}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* CTA */}
      <div className="flex flex-col sm:flex-row justify-between items-stretch sm:items-center gap-4 pt-6 border-t border-slate-200">
        <p className="text-sm text-slate-500">
          {totalVisitors > 0 && selectedDate && (!useSlotBased || selectedSlot) ? "Ready to see price?" : useSlotBased && !selectedSlot ? "Add guests, pick a date and a time slot" : "Add at least 1 guest and pick a date"}
        </p>
        <button
          type="button"
          onClick={calculateTotalPrice}
          disabled={totalVisitors === 0 || !selectedDate || loading || (useSlotBased && !selectedSlot)}
          className="px-6 py-3.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-semibold shadow-md hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        >
          {loading ? (
            <>
              <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent" />
              Calculating…
            </>
          ) : (
            <>
              Proceed to checkout
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" /></svg>
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default VisitorSelection;